import React, { useEffect } from 'react'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { buildControlsExt, doubleInput } from '../../inputs/controls'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { numberInputErrors } from '../../../utils/validationErrors/numberInputErrors'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { getInputLabel } from '../utils/getInputLabel'
import { setDefaultValue } from '../utils/setDefault'
import {
    modifyActionParameter,
    setActionInvalid,
    setActionValid,
} from '../../../store/actions/actionsActions'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { selectInvalidActionByErrorKey } from '../../../store/selectors/actionsSelectors/invalidActionsSelectors'
import { IDoubleProps } from '../../models/Double/types'
import { isEmpty } from '../utils/isEmpty'
import { useInitialEffect } from '../../../hooks/useInitialEffect'
import { useEffectAfterMount } from '../../../hooks/useEffectAfterMount'
import { PATH_JOINER } from '../../models/utils/getConfigBitPath'

export const DoubleACTN = (props: IDoubleProps) => {
    const { data = {}, dataSchema, name, wrapper, dataPath } = props
    const dispatch = useAppDispatch()

    const path = [...(dataPath.split(PATH_JOINER)), name]

    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        precision = 8,
        default: defaultValue,
    } = dataSchema

    const validationCheck = inputValidationCheck(type, dataSchema, data[name])
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isInputStateValid,
    ] = useFormValidation(data, { [name]: validationCheck })

    const debouncedInputState = useDebouncedValue(inputState, 500)
    const isActionFieldInvalid = !!useAppSelector(
        selectInvalidActionByErrorKey(path)
    )
    const isDataEmpty = isEmpty(data, name)
    const errMsg = numberInputErrors(inputState?.[name], dataSchema)
    const label = getInputLabel(name, inputLabel)
    const className = !wrapper ? 'w-100' : ''

    const controlDescriptors = [
        doubleInput({
            name,
            tooltipText,
            label,
            precision,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            placeholder: defaultValue,
        }),
    ]

    const DoubleInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    useInitialEffect(() => {
        if (isDataEmpty) {
            if (defaultValue) {
                setDefaultValue(setInputState, name, defaultValue)
            } else {
                setInputState((prev: any) => ({ ...prev, [name]: null }))
            }
        }
    })

    useEffect(() => {
        dispatch(
            modifyActionParameter({
                path,
                data: inputState,
            })
        )
    }, [debouncedInputState, dispatch])

    useEffectAfterMount(() => {
        const isCurrentValueValid = isInputStateValid()
        if (isActionFieldInvalid === isCurrentValueValid) {
            if (isCurrentValueValid) {
                dispatch(setActionValid({ path }))
            } else {
                dispatch(setActionInvalid({ path }))
            }
        }
    }, [debouncedInputState, dispatch])

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{DoubleInput}</div>
}
